import { plainToInstance, Type } from 'class-transformer'
import {
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsEnum,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsObject,
  IsOptional,
  IsString,
  Max,
  Min,
  Validate,
  ValidateNested,
} from 'class-validator'

import { IsDecimalNumber, RequireProperty } from '@agroone/helpers'
import { AreaUnit } from '../enums/area-unit.enum'
import { FarmCsrFileType } from '../enums/farm-csr-type-file.enum'
import { LevelsTrainingRegenerative } from '../enums/level-training.enum'
import { AoDocumentDto } from './ao-document.dto'

export class FarmCsr {
  id: number | null
  farmId: number | null
  levelRegenerativeAgriculture: LevelsTrainingRegenerative | null
  totalFarmArea: number | null
  totalFarmAreaUnit: AreaUnit | null
  percentageProtectionZone: number | null
  certifications: FarmCsrCertification[]
  files?: FarmCsrFile[]
  note: string | null
  farmCsrCampaign?: FarmCsrCampaign | null
  isLocked?: boolean
  documents?: AoDocumentDto[]

  constructor(farmCsr?: FarmCsr) {
    this.id = farmCsr?.id ?? null
    this.farmId = farmCsr?.farmId ?? null
    this.levelRegenerativeAgriculture = farmCsr?.levelRegenerativeAgriculture ?? null
    this.totalFarmArea = farmCsr?.totalFarmArea ?? null
    this.totalFarmAreaUnit = farmCsr?.totalFarmAreaUnit ?? null
    this.percentageProtectionZone = farmCsr?.percentageProtectionZone ?? null
    this.certifications = farmCsr?.certifications ?? []
    this.files = farmCsr?.files ?? []
    this.note = farmCsr?.note ?? null
    this.farmCsrCampaign = farmCsr?.farmCsrCampaign ?? null
    this.isLocked = farmCsr?.isLocked ?? true
    this.documents = farmCsr?.documents ?? []
  }
}

export class FarmCsrCertification {
  id!: number
  name!: string
}

export class FarmCsrFile {
  id!: number
  name!: string
  path!: string
  presignedUrl!: string
  type!: FarmCsrFileType
}

export class FarmCsrFileDto {
  date!: string
  name!: string
  content!: File
  type!: FarmCsrFileType
}

export class FarmCsrCampaignRequest {
  id!: number
  farmCsrId!: number
  campaignId!: number
  firstYearWithBonduelle!: number
  speciesLastYear!: number
  soilAreaLimitingDisturbance!: number
  soilAreaLimitingDisturbanceUnit!: string
  numberWeeksSoilCovered!: number
  percentageSemiNaturalHabitat!: number
  pollinatorDefensePlan!: boolean
  spadeTest!: boolean
  vessTest!: boolean
  humicAssessment!: boolean
  soilAnalysis!: boolean
  csrSpadeTestFieldIds!: string
  csrSpadeTestFieldNames!: string
  csrVessTestFieldIds!: string
  csrVessTestFieldNames!: string
  csrHumicAssessmentFieldIds!: string
  csrHumicAssessmentFieldNames!: string
  csrSoilAnalysisFieldIds!: string
  csrSoilAnalysisFieldNames!: string
}

export class FarmCsrCampaign {
  id: number | null
  campaignId: number | null
  farmCsrId: number | null
  firstYearWithBonduelle: number | null
  speciesLastYear: number | null
  soilAreaLimitingDisturbance: number | null
  soilAreaLimitingDisturbanceUnit: string | null
  numberWeeksSoilCovered: number | null
  percentageSemiNaturalHabitat: number | null
  pollinatorDefensePlan: boolean | null
  spadeTest: boolean | null
  vessTest: boolean | null
  humicAssessment: boolean | null
  soilAnalysis: boolean | null
  spadeTestFieldIds: number[]
  vessTestFieldIds: number[]
  humicAssessmentFieldIds: number[]
  soilAnalysisFieldIds: number[]

  constructor(farmCsrCampaign?: FarmCsrCampaign) {
    this.id = farmCsrCampaign?.id ?? null
    this.campaignId = farmCsrCampaign?.campaignId ?? null
    this.farmCsrId = farmCsrCampaign?.farmCsrId ?? null
    this.firstYearWithBonduelle = farmCsrCampaign?.firstYearWithBonduelle ?? null
    this.speciesLastYear = farmCsrCampaign?.speciesLastYear ?? null
    this.soilAreaLimitingDisturbance = farmCsrCampaign?.soilAreaLimitingDisturbance ?? null
    this.soilAreaLimitingDisturbanceUnit = farmCsrCampaign?.soilAreaLimitingDisturbanceUnit ?? null
    this.numberWeeksSoilCovered = farmCsrCampaign?.numberWeeksSoilCovered ?? null
    this.percentageSemiNaturalHabitat = farmCsrCampaign?.percentageSemiNaturalHabitat ?? null
    this.pollinatorDefensePlan = farmCsrCampaign?.pollinatorDefensePlan ?? null
    this.spadeTest = farmCsrCampaign?.spadeTest ?? null
    this.vessTest = farmCsrCampaign?.vessTest ?? null
    this.humicAssessment = farmCsrCampaign?.humicAssessment ?? null
    this.soilAnalysis = farmCsrCampaign?.soilAnalysis ?? null
    this.spadeTestFieldIds = farmCsrCampaign?.spadeTestFieldIds ?? []
    this.vessTestFieldIds = farmCsrCampaign?.vessTestFieldIds ?? []
    this.humicAssessmentFieldIds = farmCsrCampaign?.humicAssessmentFieldIds ?? []
    this.soilAnalysisFieldIds = farmCsrCampaign?.soilAnalysisFieldIds ?? []
  }
}

export class SaveFarmCsrCampaign {
  public static build(raw: any) {
    return plainToInstance(FarmCsrCampaign, raw)
  }

  @IsNumber()
  @IsOptional()
  id?: number

  @IsNumber()
  @Min(0)
  @Max(32767)
  @IsOptional()
  firstYearWithBonduelle?: number | null

  @IsNumber()
  @Min(1)
  @Max(255)
  @IsOptional()
  speciesLastYear?: number | null

  @IsNumber()
  @IsOptional()
  @Validate(IsDecimalNumber, [2])
  @RequireProperty('soilAreaLimitingDisturbanceUnit')
  soilAreaLimitingDisturbance?: number | null

  @IsString()
  @IsEnum(AreaUnit)
  @IsOptional()
  @RequireProperty()
  soilAreaLimitingDisturbanceUnit?: AreaUnit | null

  @IsNumber()
  @Min(0)
  @Max(53)
  @IsOptional()
  numberWeeksSoilCovered?: number | null

  @IsNumber()
  @Min(0)
  @Max(100)
  @IsOptional()
  percentageSemiNaturalHabitat?: number | null

  @IsBoolean()
  @IsOptional()
  pollinatorDefensePlan?: boolean

  @IsBoolean()
  @IsOptional()
  spadeTest?: boolean

  @IsBoolean()
  @IsOptional()
  vessTest?: boolean

  @IsBoolean()
  @IsOptional()
  humicAssessment?: boolean

  @IsBoolean()
  @IsOptional()
  soilAnalysis?: boolean

  @IsArray()
  @IsNumber({}, { each: true })
  @IsOptional()
  spadeTestFieldIds?: number[]

  @IsArray()
  @IsNumber({}, { each: true })
  @IsOptional()
  vessTestFieldIds?: number[]

  @IsArray()
  @IsNumber({}, { each: true })
  @IsOptional()
  humicAssessmentFieldIds?: number[]

  @IsArray()
  @IsNumber({}, { each: true })
  @IsOptional()
  soilAnalysisFieldIds?: number[]

  constructor(farmCsrCampaign?: SaveFarmCsrCampaign) {
    if (farmCsrCampaign?.id) {
      this.id = farmCsrCampaign?.id
    }
    this.firstYearWithBonduelle = farmCsrCampaign?.firstYearWithBonduelle ?? null
    this.speciesLastYear = farmCsrCampaign?.speciesLastYear ?? null
    this.soilAreaLimitingDisturbance = farmCsrCampaign?.soilAreaLimitingDisturbance ?? null
    this.soilAreaLimitingDisturbanceUnit = farmCsrCampaign?.soilAreaLimitingDisturbanceUnit ?? null
    this.numberWeeksSoilCovered = farmCsrCampaign?.numberWeeksSoilCovered ?? null
    this.percentageSemiNaturalHabitat = farmCsrCampaign?.percentageSemiNaturalHabitat ?? null
    this.pollinatorDefensePlan = farmCsrCampaign?.pollinatorDefensePlan ?? false
    this.spadeTest = farmCsrCampaign?.spadeTest ?? false
    this.vessTest = farmCsrCampaign?.vessTest ?? false
    this.humicAssessment = farmCsrCampaign?.humicAssessment ?? false
    this.soilAnalysis = farmCsrCampaign?.soilAnalysis ?? false
    this.spadeTestFieldIds = farmCsrCampaign?.spadeTestFieldIds ?? []
    this.vessTestFieldIds = farmCsrCampaign?.vessTestFieldIds ?? []
    this.humicAssessmentFieldIds = farmCsrCampaign?.humicAssessmentFieldIds ?? []
    this.soilAnalysisFieldIds = farmCsrCampaign?.soilAnalysisFieldIds ?? []
  }
}
export class SaveFarmCsr {
  public static build(raw: any) {
    return plainToInstance(SaveFarmCsr, raw)
  }

  @IsNumber()
  @IsOptional()
  id?: number

  @IsNumber()
  @IsOptional()
  farmId?: number

  @IsString()
  @IsNotEmpty()
  levelRegenerativeAgriculture: string | undefined

  @IsNumber()
  @IsNotEmpty()
  @Validate(IsDecimalNumber, [2])
  @RequireProperty('totalFarmAreaUnit')
  totalFarmArea: number | undefined

  @IsString()
  @IsEnum(AreaUnit)
  @IsNotEmpty()
  @RequireProperty('totalFarmArea')
  totalFarmAreaUnit: AreaUnit | undefined

  @IsNumber()
  @Min(0)
  @Max(100)
  @IsInt()
  percentageProtectionZone: number | undefined

  @ArrayNotEmpty()
  @IsArray()
  @IsNumber({}, { each: true })
  certificationsIds: number[] | undefined

  @IsObject()
  @ValidateNested()
  @Type(() => SaveFarmCsrCampaign)
  @IsNotEmpty()
  farmCsrCampaign!: SaveFarmCsrCampaign

  @IsString()
  @IsOptional()
  note?: string | null

  constructor(farmCsr?: SaveFarmCsr) {
    if (farmCsr?.id) {
      this.id = farmCsr?.id
    }
    this.farmId = farmCsr?.farmId
    this.levelRegenerativeAgriculture = farmCsr?.levelRegenerativeAgriculture
    this.totalFarmArea = farmCsr?.totalFarmArea
    this.totalFarmAreaUnit = farmCsr?.totalFarmAreaUnit
    this.percentageProtectionZone = farmCsr?.percentageProtectionZone
    this.certificationsIds = farmCsr?.certificationsIds
    this.note = farmCsr?.note ?? null
  }
}

export interface FarmCsrRawValue {
  id?: number
  farmLevelTraining: string
  totalFarmArea: number
  totalFarmAreaUnit: AreaUnit
  farmCertifications: number[]
  percentageProtectionZone: number
  csrNote?: string
  startingYear?: number
  speciesLastYear?: number
  farmAreaDisturbance?: number
  farmAreaDisturbanceUnit?: AreaUnit
  weeksCovered?: number
  semiNaturalPercent?: number
  pollinatorPlan?: boolean
  spadeTestDone?: boolean
  vessTestDone?: boolean
  humicAssessmentDone?: boolean
  soilAnalysisDone?: boolean
  spadeTestField?: number[]
  vessTestField?: number[]
  humicAssessmentField?: number[]
  soilAnalysisField?: number[]
}

export class FarmCsrDto {
  id?: number
  levelRegenerativeAgriculture: string | undefined
  totalFarmArea: number | undefined | null
  totalFarmAreaUnit: AreaUnit | undefined | null
  percentageProtectionZone: number | undefined | null
  note: string | undefined | null
  certificationsIds?: number[]

  constructor(farmCsr?: SaveFarmCsr) {
    if (farmCsr?.id) {
      this.id = farmCsr?.id
    }
    this.levelRegenerativeAgriculture = farmCsr?.levelRegenerativeAgriculture
    this.totalFarmArea = farmCsr?.totalFarmArea
    this.totalFarmAreaUnit = farmCsr?.totalFarmAreaUnit
    this.percentageProtectionZone = farmCsr?.percentageProtectionZone
    this.note = farmCsr?.note
  }
}

export class FarmCsrCampaignDto {
  id?: number
  campaignId?: number
  farmCsrId?: number
  firstYearWithBonduelle?: number | null
  speciesLastYear?: number | null
  soilAreaLimitingDisturbance?: number | null
  soilAreaLimitingDisturbanceUnit?: string | null
  numberWeeksSoilCovered?: number | null
  percentageSemiNaturalHabitat?: number | null
  pollinatorDefensePlan?: boolean
  spadeTest?: boolean
  vessTest?: boolean
  humicAssessment?: boolean
  soilAnalysis?: boolean

  constructor(farmCsrCampaign?: SaveFarmCsrCampaign) {
    if (farmCsrCampaign?.id) {
      this.id = farmCsrCampaign?.id
    }
    this.firstYearWithBonduelle = farmCsrCampaign?.firstYearWithBonduelle ?? null
    this.speciesLastYear = farmCsrCampaign?.speciesLastYear ?? null
    this.soilAreaLimitingDisturbance = farmCsrCampaign?.soilAreaLimitingDisturbance ?? null
    this.soilAreaLimitingDisturbanceUnit = farmCsrCampaign?.soilAreaLimitingDisturbanceUnit ?? null
    this.numberWeeksSoilCovered = farmCsrCampaign?.numberWeeksSoilCovered ?? null
    this.percentageSemiNaturalHabitat = farmCsrCampaign?.percentageSemiNaturalHabitat ?? null
    this.pollinatorDefensePlan = farmCsrCampaign?.pollinatorDefensePlan ?? false
    this.spadeTest = farmCsrCampaign?.spadeTest ?? false
    this.vessTest = farmCsrCampaign?.vessTest ?? false
    this.humicAssessment = farmCsrCampaign?.humicAssessment ?? false
    this.soilAnalysis = farmCsrCampaign?.soilAnalysis ?? false
  }
}
